import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import React from "react";
import { PiCaretCircleLeftThin, PiCaretCircleRightThin } from "react-icons/pi";
import { container_classname } from "./constants";
import ExternalLinkButton from "./ExternalLinkButton";
import { StatsInfoProps } from "./IndustriesSection/StatsInfo";

export interface Feature {
    mediaUrl: string;
    chiptitle: string;
    title: string;
    statsInfoData: Array<StatsInfoProps>;
    url: string;
    length?: boolean;
    cardColor?: boolean;
}

const FeatureCard: React.FC<Feature> = (props) => {
    return (
        <div className={`keen-slider__slide  flex md:flex-row flex-col gap-0 rounded ${props.length ? "" : "mx-auto lg:mx-0"}`}>
            <div className="md:w-1/2 w-full rounded-tl rounded-bl overflow-hidden lg:h-80 h-64">
                <Image src={props.mediaUrl} alt={props.title} width={401} height={380} style={{ width: "100%", height: "100%", objectFit: "fill" }} />
            </div>

            <div className={`w-full lg:h-80 h-72 ${props.cardColor ? "bg-gray-200" : "bg-white"} p-8  flex flex-col`}>
                <p className="text-gray-600 text-sm mb-3">{props.chiptitle}</p>
                <h3 className="md:text-2xl text-lg font-semibold md:mb-2  mb-3" color="#151515">
                    {props.title}
                </h3>

                <div className="md:flex md:flex-row hidden md:gap-14 gap-1 md:mb-5 mb-3">
                    {props.statsInfoData.map((stats, idx) => (
                        <div key={idx} className="border-l-4 border-blue-600 border-solid pl-5">
                            <p className="font-bold md:text-6xl text-2xl" style={{ fontFamily: "Degular Variable" }}>
                                {stats.percentage}
                            </p>
                            <p>{stats.label}</p>
                        </div>
                    ))}
                </div>
                <div className="flex-grow"></div>
                <ExternalLinkButton size="sm" href={props.url}>
                    Read the story
                </ExternalLinkButton>
            </div>
        </div>
    );
};

const CaseStudyFeaturesSection: React.FC<{ features: Feature[]; backgroundColor?: boolean }> = ({ features, backgroundColor }) => {
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [loaded, setLoaded] = React.useState(false);
    const [sliderRef, instanceRef] = useKeenSlider({
        mode: "free",
        drag: true,
        // rubberband: true,
        vertical: false,
        slides: {
            perView: 1,
            spacing: 20,
        },
        breakpoints: {
            "(max-width: 640px)": {
                slides: { perView: 1.1, spacing: 20 },
            },
        },
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    const disableLeftArrow = React.useMemo(() => currentSlide === 0, [currentSlide]);
    const disableRightArrow = React.useMemo(() => {
        const slidesLength = features.length;
        return slidesLength !== undefined && currentSlide === slidesLength - 1;
    }, [currentSlide, features]);

    return (
        <section
            className="lg:py-20 py-16"
            style={{
                background: backgroundColor ? " " : "linear-gradient(283deg, #396AFF 0%, #000 98.82%)",
            }}
        >
            <div className={`${container_classname} flex flex-col lg:gap-12 gap-8`}>
                <div className="flex justify-between">
                    <div className="flex flex-col gap-6">
                        <h2
                            className={`lg:text-5xl text-3xl font-semibold ${backgroundColor ? "text-black" : "text-white"} lg:max-w-5xl`}
                            style={{ fontFamily: "Degular Display" }}
                        >
                            Customer stories
                        </h2>
                    </div>
                    {loaded && instanceRef.current && features.length > 1 && (
                        <div className="lg:flex hidden text-white">
                            <PiCaretCircleLeftThin
                                style={{ cursor: disableLeftArrow ? "not-allowed" : "pointer" }}
                                size={60}
                                onClick={(e: any) => {
                                    if (!disableLeftArrow) e.stopPropagation() || instanceRef.current?.prev();
                                }}
                            />
                            <PiCaretCircleRightThin
                                style={{ cursor: disableRightArrow ? "not-allowed" : "pointer" }}
                                size={60}
                                onClick={(e: any) => {
                                    if (!disableRightArrow) e.stopPropagation() || instanceRef.current?.next();
                                }}
                            />
                        </div>
                    )}
                </div>
                <div ref={sliderRef} className={"keen-slider w-full"} style={{ overflow: "visible" }}>
                    {features.map((feature, index) => (
                        <FeatureCard key={index} {...feature} length={features.length > 1} />
                    ))}
                </div>
                {loaded && instanceRef.current && features.length > 1 && (
                    <div className="lg:hidden mx-auto flex gap-1 text-white">
                        <PiCaretCircleLeftThin
                            style={{ cursor: disableLeftArrow ? "not-allowed" : "pointer" }}
                            size={60}
                            onClick={(e: any) => {
                                if (!disableLeftArrow) e.stopPropagation() || instanceRef.current?.prev();
                            }}
                        />
                        <PiCaretCircleRightThin
                            style={{ cursor: disableRightArrow ? "not-allowed" : "pointer" }}
                            size={60}
                            onClick={(e: any) => {
                                if (!disableRightArrow) e.stopPropagation() || instanceRef.current?.next();
                            }}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

export default CaseStudyFeaturesSection;
